/**
 * ============================================================================
 * [P3-04-C] URLList - 드래그 앤 드롭 URL 목록 컴포넌트
 *
 * CRITICAL:
 * - @dnd-kit/core와 @dnd-kit/sortable 사용
 * - 접근성을 위한 KeyboardSensor 포함
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { SortableURLCard } from './SortableURLCard'
import type { URLLink } from '@/types'
import styles from './URLLink.module.css'

// ============================================================================
// 2. Types
// ============================================================================
interface URLListProps {
  urls: URLLink[]
  onReorder: (activeId: string, overId: string) => void
  onEdit: (id: string) => void
  onDelete: (id: string) => void
}

// ============================================================================
// 3. URLList Component
// ============================================================================
export function URLList({ urls, onReorder, onEdit, onDelete }: URLListProps) {
  // ========================================================================
  // 3-1. 센서 설정 (포인터 + 키보드)
  // ========================================================================
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px 이동해야 드래그 시작 (클릭과 구분)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // ========================================================================
  // 3-2. 드래그 종료 핸들러
  // ========================================================================
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      onReorder(String(active.id), String(over.id))
    }
  }

  // ========================================================================
  // 3-3. 빈 상태 렌더링
  // ========================================================================
  if (urls.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>저장된 링크가 없습니다.</p>
        <p>자주 사용하는 링크를 추가해보세요!</p>
      </div>
    )
  }

  // ========================================================================
  // 3-4. 드래그 앤 드롭 목록 렌더링
  // ========================================================================
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={urls.map((u) => u.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className={styles.urlList}>
          {urls.map((url) => (
            <SortableURLCard
              key={url.id}
              url={url}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

export default URLList
